import React, { useState } from "react";
import { X, Check, Plus } from "lucide-react";
import { skillGapAnalysisService } from "../../services/skillGapAnalysisService";
import { useSelector, useDispatch } from "react-redux";
import { setSkillsGapCount, setSkillsGap } from "../../redux/userSlice";
import { toast } from "react-toastify";

const ComparisonView = ({
  state,
  setPartial,
  targetOcc,
  targetOccupationFromRedux,
  targetJob,
  isSkillMatching,
  getSkillsCount,
  fetchTargetOccupationSkills
}) => {
  const [isAddingToPlan, setIsAddingToPlan] = useState(false);
  const [isAddedToPlan, setIsAddedToPlan] = useState(false);
  const { id: userId } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const handleAddToLearningPlan = async () => {
    if (!userId) {
      toast.error("Please log in to add to learning plan");
      return;
    }

    setIsAddingToPlan(true);
    try {
      const skillsGap = [];
      if (state.skillsData) {
        Object.entries(state.skillsData).forEach(([category, skills]) => {
          skills.forEach(skill => {
            if (!isSkillMatching(skill)) {
              skillsGap.push(skill); 
            }
          });
        });
      }

      const response = await skillGapAnalysisService.addToLearningPlan();
      
      if (response.success) {
        setIsAddedToPlan(true);
        
        dispatch(setSkillsGapCount(skillsGap.length));
        dispatch(setSkillsGap(skillsGap));
        
      } else {
        toast.error(response.error || "Failed to add to learning plan");
      }
    } catch (error) {
      console.error("Error adding to learning plan:", error);
    } finally {
      setIsAddingToPlan(false);
    }
  };

  const handleExportResults = () => {
    try {
      const matchingSkills = [];
      const skillsGap = [];
      
      if (state.skillsData) {
        Object.entries(state.skillsData).forEach(([category, skills]) => {
          skills.forEach(skill => {
            if (isSkillMatching(skill)) {
              matchingSkills.push(skill);
            } else {
              skillsGap.push(skill);
            }
          });
        });
      }
      
      const excelData = [
        ["Matching Skills", "Gap Skills"]
      ];
      
      const maxLength = Math.max(matchingSkills.length, skillsGap.length);
      
      for (let i = 0; i < maxLength; i++) {
        const matchingSkill = matchingSkills[i] || "";
        const gapSkill = skillsGap[i] || "";
        excelData.push([matchingSkill, gapSkill]);
      }
      
      const csvContent = excelData.map(row => 
        row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(',')
      ).join('\n');
      
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { 
        type: 'text/csv;charset=utf-8;' 
      });
      
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      const targetOccupation = state.targetOccupation || targetOcc || targetOccupationFromRedux || targetJob || "Unknown";
      link.setAttribute('href', url);
      link.setAttribute('download', `Skills_Analysis_${targetOccupation.replace(/[^a-zA-Z0-9\s]/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      
      console.log("Excel file exported successfully");
    } catch (error) {
      console.error("Error exporting file:", error);
      alert("Error exporting file. Please try again.");
    }
  };
  const renderStats = () => (
    <div className="bg-gray-50 border rounded-lg p-4 mb-4 grid grid-cols-2 text-center">
      <div>
        <div className="text-2xl font-bold text-blue-600">{getSkillsCount(true, state.skillsData)}</div>
        <p className="text-xs font-bold text-gray-600">Matching Skills</p>
      </div>
      <div>
        <div className="text-2xl font-bold text-red-600">{getSkillsCount(false, state.skillsData)}</div>
        <p className="text-xs font-bold text-gray-600">Skills Gap</p>
      </div>
    </div>
  );

  const renderSkillsList = () => (
    <div className="space-y-4">
      {Object.entries(state.skillsData).map(([cat, skills]) => (
        <div key={cat} className="border rounded-lg p-3">
          <h4 className="font-semibold text-[#6e28cc] mb-2 text-sm">{cat}</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {skills.map((skill, i) => {
              const match = isSkillMatching(skill);
              if ((state.activeTab === "matching" && !match) || (state.activeTab === "gap" && match)) return null;
              return (
                <div 
                  key={i} 
                  className={`flex items-center gap-2 p-2 border rounded-lg text-sm ${
                    match 
                      ? "border-gray-300 text-gray-700" 
                      : "border-red-400 text-red-600"
                  }`}
                >
                  {match ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                  {skill}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  const renderContent = () => {
    if (state.skillsError) {
      return (
        <div className="text-center py-8">
          <p className="text-yellow-800 mb-2">{state.skillsError}</p>
          <button 
            onClick={() => {
              const targetToUse = targetOcc || state.targetOccupation || targetOccupationFromRedux || targetJob;
              const career = state.targetOccupationOptions.find(c => c.name === targetToUse);
              if (career) {
                setPartial({ skillsError: null, isLoadingSkills: true });
                fetchTargetOccupationSkills(targetToUse, career.type);
              }
            }} 
            className="text-sm text-[#6e28cc] hover:underline"
          >
            Try again
          </button>
        </div>
      );
    }
    
    if (!state.skillsData) {
      return <p className="text-center py-8 text-gray-500">No skills data available.</p>;
    }

    return (
      <>
        {renderStats()}
        <div className="flex gap-2 mb-4">
          {["matching", "gap"].map(tab => (
            <button 
              key={tab} 
              onClick={() => setPartial({ activeTab: tab })}
              className={`flex items-center gap-1 px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                state.activeTab === tab 
                  ? (tab === "matching" ? "bg-blue-600 text-white" : "bg-red-600 text-white") 
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {tab === "matching" ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
              {tab[0].toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
        {renderSkillsList()}
      </>
    );
  };

  return (
    <>
      <div className="p-4 border-b flex items-center justify-center">
        <div className="bg-purple-100 rounded-xl p-3 text-center">
          <div className="text-xs text-purple-700 mb-1">Target Occupation</div>
          <div className="font-semibold text-sm text-purple-800">{state.targetOccupation}</div>
        </div>
      </div>
      <div className="p-4">{renderContent()}</div>
      <div className="p-4 bg-gray-50 flex flex-col sm:flex-row gap-3">
        <button
          onClick={handleAddToLearningPlan}
          disabled={isAddingToPlan || isAddedToPlan || !userId}
          className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
            isAddedToPlan
              ? "bg-blue-100 text-blue-700 border border-blue-300 cursor-default"
              : isAddingToPlan || !userId
              ? "bg-gray-200 text-gray-400 cursor-not-allowed opacity-50"
              : "bg-purple-600 text-white hover:bg-purple-700 cursor-pointer"
          }`}
        >
          {isAddingToPlan ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Adding to Learning Plan...
            </>
          ) : isAddedToPlan ? (
            <>
              <Check className="w-4 h-4" />
              Added to Learning Plan
            </>
          ) : (
            <>
              <Plus className="w-4 h-4" />
              Add Skills Gap To My Learning Plan
            </>
          )}
        </button>
        <button
          onClick={handleExportResults}
          className="flex-1 border bg-white hover:bg-gray-50 py-2 px-3 rounded-lg text-sm font-medium text-black flex items-center justify-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Export Results
        </button>
      </div>
    </>
  );
};

export default ComparisonView;
